import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";

import { useCardController, useDialog } from "@datanac/datanac-mui-toolkit";
import { Box, Chip, Divider, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { useArcCoCountyYieldOverrides } from "api-toolkit/useArcCoCountyYieldOverrides";
import { useArcPlc } from "api-toolkit/useArcPlc";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { ArcPlcIcon, InfoIcon } from "components/Icons/Icons";
import { getUniqueBaseAcreCrops } from "components/Vault/Farm/helpers";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { useSnackbar } from "notistack";
import { formatPrice } from "utility/formatters";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { TilleyArcPlcCard } from "../Cards/TilleyArcPlcCard";
import ArcPlcCountyCropCard from "./components/ArcPlcCountyCropCard";
import ArcPlcProducerFarmBaseAcreDataTable from "./components/ArcPlcProducerFarmBaseAcreDataTable";

export default function ArcPlcCountyWorkspace() {
  const { globalState } = useContext(AppContext);
  const { crop_year, location_state_name, location_county_name } = useParams();

  const { enqueueSnackbar } = useSnackbar();
  const { component: confirmDialogComponent } = useDialog();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBaseAcres, setIsLoadingBaseAcres] = useState(true);

  const CARD_GRID_KEY = "arc_plc_county";
  const [workspaceMode, setWorkspaceMode] = useState(null);
  const { addAttachments } = useChatAssistantAttachments();
  const { state: producerFarmCrops, actions: producerFarmCropActions } = useCardController();
  const { arcCoCountyYieldOverrides } = useArcCoCountyYieldOverrides({ crop_year });

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadData = async () => {
    try {
      setIsLoading(true);

      const producerFilter = {
        producer_token: globalState.producer_token,
        location_state_name,
        location_county_name,
        crop_year,
        is_active: true,
      };
      const farms = await UsersApiHelper.selectProducerFarms(producerFilter, globalState);
      producerFarmCropActions.replace(farms);

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      producerFarmCropActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [globalState?.producer_token]);

  // --- --- ---

  const { state: baseAcres, actions: baseAcreActions } = useCardController([]);

  const arcPlc = useArcPlc({
    baseAcres: baseAcres.reduce((accumulator, currentValue) => {
      return [...accumulator, ...currentValue.baseAcres];
    }, []),
    crop_year,
    arcCoCountyYieldOverrides,
  });

  const loadBaseAcres = async () => {
    try {
      setIsLoadingBaseAcres(true);

      const baseAcresFilter = {
        crop_year,
        producer_token: globalState.producer_token,
        location_state_name,
        location_county_name,
        is_active: true,
      };
      const baseAcres = await UsersApiHelper.selectProducerFarmBaseAcres(baseAcresFilter, globalState);

      const _uniqueCrops = getUniqueBaseAcreCrops(baseAcres);

      baseAcreActions.replace(_uniqueCrops);

      setIsLoadingBaseAcres(false);
    } catch (err) {
      setIsLoadingBaseAcres(false);
      baseAcreActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (location_state_name && location_county_name && globalState?.producer_token) {
      loadBaseAcres();
    }
  }, [location_state_name, location_county_name, globalState?.producer_token]);

  const calculateCropTotals = (_currentBaseAcres) => {
    const _arcplc = arcPlc?.crops?.find((_crop) => {
      const commodityMatches = _crop.commodity_name === _currentBaseAcres?.commodity_name;
      const typeMatches = (_crop.type_name || null) === (_currentBaseAcres?.type_name || null);
      return commodityMatches && typeMatches;
    });
    return _arcplc;
  };

  useEffect(() => {
    addAttachments([
      {
        name: "ARC/PLC - Program Comparison by County",
        content: arcPlc,
      },
    ]);
  }, [arcPlc?.totals?.arcPayment, arcPlc?.totals?.plcPayment]);

  // --- --- ---

  return (
    <>
      <WorkspaceHeader
        title={
          location_state_name && location_county_name ? location_county_name + ", " + location_state_name : "ARC/PLC"
        }
        category={{
          title: "ARC/PLC",
          icon: <ArcPlcIcon />,
        }}
        breadCrumbs={[
          {
            title: "ARC/PLC " + crop_year,
            path: "/arcplc",
          },
        ]}
      />

      <Typography variant="h1">Program Comparison by County</Typography>

      <Grid container spacing={2} className="base-acres">
        <Grid item xs={12} md={6} lg={4} xl={3}>
          <TilleyArcPlcCard
            customContent={
              <>
                <Stack
                  direction="column"
                  className="custom-content"
                  sx={{
                    height: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      flexGrow: 1,
                    }}
                  >
                    {!(arcPlc?.isLoading && (isLoadingBaseAcres || !!baseAcres?.length)) && (
                      <>
                        {arcPlc?.totals?.arcPayment === null && <Typography variant="h1">--</Typography>}
                        {Math.round(arcPlc?.totals?.arcPayment) === 0 && (
                          <Chip
                            label={
                              <Stack direction="column" alignItems="center" spacing={0.5}>
                                <InfoIcon />
                                <Typography variant="caption" align="center">
                                  No ARC-CO payment projected
                                </Typography>
                              </Stack>
                            }
                          />
                        )}
                        {Math.round(arcPlc?.totals?.arcPayment) > 0 && (
                          <Typography variant="h1" className="price">
                            {formatPrice(arcPlc?.totals?.arcPayment, 0)}
                          </Typography>
                        )}
                      </>
                    )}
                    {arcPlc?.isLoading && (isLoadingBaseAcres || !!baseAcres?.length) && (
                      <Typography variant="h1" className="skeleton">
                        <Skeleton variant="rectangular" width={150} height={50} sx={{ bgcolor: "text.secondary" }} />
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Typography variant="body1" align="center">
                  ARC-CO
                  <br />
                  PAYMENT
                </Typography>
              </>
            }
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4} xl={3}>
          <TilleyArcPlcCard
            customContent={
              <>
                <Stack
                  direction="column"
                  className="custom-content"
                  sx={{
                    height: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      flexGrow: 1,
                    }}
                  >
                    {!(arcPlc?.isLoading && (isLoadingBaseAcres || !!baseAcres?.length)) && (
                      <>
                        {arcPlc?.totals?.plcPayment === null && <Typography variant="h1">--</Typography>}
                        {Math.round(arcPlc?.totals?.plcPayment) === 0 && (
                          <Chip
                            label={
                              <Stack direction="column" alignItems="center" spacing={0.5}>
                                <InfoIcon />
                                <Typography variant="caption" align="center">
                                  No PLC payment projected
                                </Typography>
                              </Stack>
                            }
                          />
                        )}
                        {Math.round(arcPlc?.totals?.plcPayment) > 0 && (
                          <Typography variant="h1" className="price">
                            {formatPrice(arcPlc?.totals?.plcPayment, 0)}
                          </Typography>
                        )}
                      </>
                    )}
                    {arcPlc?.isLoading && (isLoadingBaseAcres || !!baseAcres?.length) && (
                      <Typography variant="h1" className="skeleton">
                        <Skeleton variant="rectangular" width={150} height={50} sx={{ bgcolor: "text.secondary" }} />
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Typography variant="body1">
                  PLC
                  <br />
                  PAYMENT
                </Typography>
              </>
            }
          />
        </Grid>
      </Grid>

      <Divider />

      <WorkspacePageHeader
        title="Crops"
        viewMode={workspaceMode}
        onViewModeChange={setWorkspaceMode}
        cardGridKey={CARD_GRID_KEY}
        isLoading={isLoadingBaseAcres}
      />

      {workspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {baseAcres.map((currentBaseAcres, index) => {
            const _currentArcPlc = calculateCropTotals(currentBaseAcres);
            return (
              <ArcPlcCountyCropCard
                key={index}
                producerFarm={producerFarmCrops}
                baseAcres={currentBaseAcres}
                arcPlc={_currentArcPlc}
                isLoadingArcPlc={arcPlc?.isLoading}
                isErrorArcPlc={arcPlc?.isError}
              />
            );
          })}
          {!baseAcres?.length && (
            <EmptyCard
              text="No base acres listed"
              secondaryText="Setup base acres in the farm vault"
              isLoading={isLoading}
            />
          )}
        </Box>
      )}

      {workspaceMode === WORKSPACE_VIEW_TABLE && (
        <ArcPlcProducerFarmBaseAcreDataTable
          producerFarm={producerFarmCrops}
          producerFarmBaseAcres={baseAcres}
          isLoading={isLoadingBaseAcres}
        />
      )}

      {confirmDialogComponent}
      <WorkspaceActionBar content={<></>} />
    </>
  );
}
